import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { showError, showToast } from "../constants";
import { generateEncryptedKeyBody } from "../utils/crypto";
import { CommonBody } from "../types/General";
import {
  useLazyGetDemoToolsConfigQuery,
  useDemoPropertyActionMutation,
  useDemoUserActionMutation,
  useDemoRefundActionMutation,
} from "../services/demoTools";

type Variant = "property" | "user" | "refund";

type Props = {
  variant: Variant;
  entityId?: string;
  context?: Record<string, any>;
  onSuccess?: () => void;
};

type ActionDef = {
  key: string;
  label: string;
  confirm: string;
  note?: string;
  needsCredits?: boolean;
};

const PROPERTY_ACTIONS: ActionDef[] = [
  {
    key: "rtaFirstTimeout",
    label: "Force RTA 1st timeout (3 options)",
    confirm:
      "Force first Request-to-Agent timeout? The owner will get the 3-option notification.",
  },
  {
    key: "rtaSecondTimeout",
    label: "Force RTA 2nd timeout (2 options)",
    confirm:
      "Force second Request-to-Agent timeout? Only Refund + Highlight options will show.",
  },
  {
    key: "rtaAutoPublish",
    label: "Force auto-publish as Highlight",
    confirm:
      "Auto-publish this listing as Highlight (same as 24h no-action cron)?",
  },
  {
    key: "demoteHighlight",
    label: "Demote Highlight → Normal",
    confirm:
      "Demote Highlight to Normal? (No transition push — product config)",
    note: "Silent demote — matches production cron.",
  },
  {
    key: "expireListing",
    label: "Force listing Expire",
    confirm:
      "Expire this listing now? (No expiration push — product config)",
    note: "Silent expire — matches production cron.",
  },
  {
    key: "softDeleteProperty",
    label: "Force soft-delete (Save later / Expired)",
    confirm:
      "Soft-delete this Save-for-later or Expired listing now (same as 30-day retention cron)?",
  },
];

const USER_ACTIONS: ActionDef[] = [
  {
    key: "expireMembership",
    label: "Force plan expire",
    confirm: "Expire active membership and notify the user?",
  },
  {
    key: "membershipReminder",
    label: "Send plan expiry reminder",
    confirm: "Send membership expiry reminder push/email now?",
  },
  {
    key: "grantCredits",
    label: "Grant wallet credits",
    confirm:
      "Grant the entered normal/highlight credits (creates a 30-day credit purchase record)?",
    needsCredits: true,
  },
  {
    key: "expireWalletCredits",
    label: "Force expire wallet credits",
    confirm:
      "Expire all active wallet credit purchases for this user now? Credits are removed and a push is sent (same as 30-day cron).",
  },
  {
    key: "extendListingsValidity",
    label: "Extend listing validity",
    confirm:
      "Refresh publishedAt on all published listings (restart 30-day life)?",
  },
  {
    key: "passMembershipDays",
    label: "Pass +30 Days",
    confirm:
      "Simulate 30 days passing? Plan end date moves back by 30 days. Unused credits are removed only if that credit’s own end date has now passed; still-active credits stay.",
  },
];

const REFUND_ACTIONS: ActionDef[] = [
  {
    key: "resendWhatsApp",
    label: "Resend refund WhatsApp",
    confirm:
      "Resend WhatsApp for this refund (admin request or user processed template)?",
  },
];

const DemoToolsPanel = ({ variant, entityId, context, onSuccess }: Props) => {
  const [getConfig] = useLazyGetDemoToolsConfigQuery();
  const [propertyAction, { isLoading: propLoading }] =
    useDemoPropertyActionMutation();
  const [userAction, { isLoading: userLoading }] = useDemoUserActionMutation();
  const [refundAction, { isLoading: refundLoading }] =
    useDemoRefundActionMutation();

  const [enabled, setEnabled] = useState(false);
  const [pending, setPending] = useState<ActionDef | null>(null);
  const [normalCred, setNormalCred] = useState("1");
  const [highlightCred, setHighlightCred] = useState("1");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getConfig().unwrap();
        if (mounted && res?.statusCode === 200) {
          setEnabled(Boolean(res?.data?.enabled));
        }
      } catch {
        if (mounted) setEnabled(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [getConfig]);

  if (!enabled || !entityId) return null;

  const actions =
    variant === "property"
      ? PROPERTY_ACTIONS
      : variant === "user"
        ? USER_ACTIONS
        : REFUND_ACTIONS;

  const busy = propLoading || userLoading || refundLoading;

  const runAction = async (action: ActionDef) => {
    try {
      const body: Record<string, any> = { action: action.key };
      if (action.needsCredits) {
        body.normalCred = Number(normalCred) || 0;
        body.highlightCred = Number(highlightCred) || 0;
      }
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

      let res: any;
      if (variant === "property") {
        res = await propertyAction({
          id: entityId,
          encryptedBody,
        }).unwrap();
      } else if (variant === "user") {
        res = await userAction({ id: entityId, encryptedBody }).unwrap();
      } else {
        res = await refundAction({ id: entityId, encryptedBody }).unwrap();
      }

      const note = res?.data?.note;
      showToast(
        note
          ? `${res?.message || "Done"}. ${note}`
          : res?.message || "Demo action applied",
      );
      setPending(null);
      onSuccess?.();
    } catch (error: any) {
      showError(
        error?.data?.message || error?.message || "Demo action failed",
      );
    }
  };

  return (
    <Card
      className="cards"
      sx={{
        mt: 2,
        border: "2px solid #b45309",
        backgroundColor: "#fff7ed",
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <WarningAmberIcon sx={{ color: "#b45309" }} />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ color: "#9a3412" }}
          >
            Demo Tools — Staging Only
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ mb: 2, color: "#44403c" }}
        >
          Force time-based lifecycle steps for live client testing. Uses the
          same backend logic as cron.
        </Typography>

        {context && Object.keys(context).length > 0 ? (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "#ffffff",
              border: "1px solid #e7e5e4",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#57534e", fontWeight: 600 }}
            >
              Current snapshot
            </Typography>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              {Object.entries(context).map(([k, v]) => (
                <Grid item xs={12} sm={6} md={4} key={k}>
                  <Typography variant="body2" sx={{ color: "#1c1917" }}>
                    <strong>{k}:</strong>{" "}
                    {v === undefined || v === null || v === ""
                      ? "-"
                      : String(v)}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}

        {variant === "user" ? (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <TextField
                size="small"
                label="Normal credits"
                type="number"
                fullWidth
                value={normalCred}
                onChange={(e) => setNormalCred(e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                size="small"
                label="Highlight credits"
                type="number"
                fullWidth
                value={highlightCred}
                onChange={(e) => setHighlightCred(e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        ) : null}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {actions.map((action) => (
            <Button
              key={action.key}
              variant="contained"
              disabled={busy}
              onClick={() => setPending(action)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#ffffff !important",
                backgroundColor: "#c2410c !important",
                border: "1px solid #9a3412",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#9a3412 !important",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  color: "#ffffff !important",
                  backgroundColor: "#fdba74 !important",
                  opacity: 0.7,
                },
              }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      </CardContent>

      <Dialog open={Boolean(pending)} onClose={() => setPending(null)}>
        <DialogTitle sx={{ color: "#1c1917" }}>Confirm demo action</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#1c1917" }}>{pending?.confirm}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPending(null)}
            disabled={busy}
            sx={{ color: "#1c1917" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy || !pending}
            onClick={() => pending && runAction(pending)}
            sx={{
              color: "#ffffff !important",
              backgroundColor: "#c2410c !important",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#9a3412 !important" },
            }}
          >
            Run
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default DemoToolsPanel;
